import React, { useState, useEffect, useCallback } from 'react';
import { useUserDetailsApi } from '../../../hooks/useUserDetails';
import { useAccountActionContext } from '../../../contexts/AccountActionContext';
import { useEnhancedAuth } from '../../../contexts/EnhancedAuthContext';
import EditAgency from './EditAgency';
import profileStyles from '../../../styles/AccountProfile.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';
import AgencyUsers from './AgencyUsers';
import { PermissionGate } from '../../common/PermissionGate';

interface AgencyInformationProps {
    userDetails: any;
}

const getInitials = (name?: string | null): string => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return parts.slice(0, 2).map(p => p[0]?.toUpperCase() || '').join('') || '?';
};

/**
 * Maps the agency's actual computed payment status to a badge — replaces
 * the old hardcoded "Active" label, which showed regardless of whether the
 * agency was pending, on trial, expired, or genuinely active.
 */
const getStatusBadge = (agency: any): { label: string; className: string } => {
    const status = agency?.paymentStatus;
    if (status === 'active') {
        return { label: 'Active', className: profileStyles.badgeSuccess };
    }
    if (status === 'trial') {
        return agency?.isPaymentValid
            ? { label: 'Trial', className: profileStyles.badgeNeutral }
            : { label: 'Trial Expired', className: profileStyles.badgeDanger };
    }
    if (status === 'pending') {
        return { label: 'Pending Setup', className: profileStyles.badgeWarning };
    }
    if (status === 'expired') {
        return { label: 'Expired', className: profileStyles.badgeDanger };
    }
    return { label: status || 'Unknown', className: profileStyles.badgeNeutral };
};

const AgencyInformation: React.FC<AgencyInformationProps> = ({ userDetails }) => {
    const [showEditAgency, setShowEditAgency] = useState(false);
    const [agencyUsers, setAgencyUsers] = useState<any[]>([]);
    const [pendingInvites, setPendingInvites] = useState<any[]>([]);
    const [inviteLoading, setInviteLoading] = useState(false);
    const [removeLoading, setRemoveLoading] = useState<string | null>(null);
    
    // Phase 2: Use enhanced auth for permission checking
    const { hasPermission, userDetails: enhancedUserDetails } = useEnhancedAuth();
    const canManageUsers = hasPermission('user_manage_agency');
    const { fetchAgencyUsers, sendUserInvitation, fetchPendingAgencyInvites, cancelUserInvite, removeAgencyUsers, deleteAgencyUser } = useAccountActionContext();
    
    // Use enhanced user details, fallback to legacy prop during transition
    const currentUserDetails = enhancedUserDetails || userDetails;
    const fetchUsers = useCallback(async () => {
        if (!currentUserDetails?.agency?.agencyId || !canManageUsers) return;
        try {
            let users = await fetchAgencyUsers(currentUserDetails.agency.agencyId);
            users = users.filter((user: any) => user.id !== currentUserDetails.id); // Exclude current user
            setAgencyUsers(users || []);
        } catch (err) {
            console.error('Error fetching agency users:', err);
            setAgencyUsers([]);
        }
    }, [currentUserDetails?.agency?.agencyId, canManageUsers, fetchAgencyUsers, setAgencyUsers, currentUserDetails.id]);

    const fetchInvites = useCallback(async () => {
        if (!currentUserDetails?.agency?.agencyId || !canManageUsers) return;
        try {
            const invites = await fetchPendingAgencyInvites(currentUserDetails.agency.agencyId);
            setPendingInvites(invites || []);
        } catch (err) {
            console.error('Error fetching pending invites:', err);
            setPendingInvites([]);
        }
    }, [currentUserDetails?.agency?.agencyId, canManageUsers, fetchPendingAgencyInvites, setPendingInvites]);

    const cancelInvite = async (inviteId: string) => {
        try {
            await cancelUserInvite(inviteId);
            await fetchInvites();
            alert('Invitation cancelled successfully!');
        } catch (err) {
            alert('Error cancelling invitation. Please try again.');
        }
    };

    useEffect(() => {
        fetchUsers();
        fetchInvites();
    }, [fetchUsers, fetchInvites]);

    const handleRemoveUser = async (userId: string) => {
        setRemoveLoading(userId);
        try {
            await removeAgencyUsers(userDetails.agency.agencyId, [userId], userDetails.id);
            await fetchUsers();
        } catch (err) {
            alert('Error removing user.');
        } finally {
            setRemoveLoading(null);
        }
    };

    const handleInviteUser = async (email: string, role: string) => {
        setInviteLoading(true);
        const inviteData = {
            email,
            agencyId: userDetails?.agency?.agencyId || '',
            role,
            senderEmail: userDetails?.email || ''
        };
        try {
            await sendUserInvitation(inviteData);
            await fetchInvites();
            await fetchUsers();
            alert('Invitation sent successfully!');
        } catch (err) {
            alert('Error sending invitation. Please try again.');
        } finally {
            setInviteLoading(false);
        }
    };

    const statusBadge = getStatusBadge(userDetails.agency);

    return (
        <div className={profileStyles.card}>
            <div className={profileStyles.cardHeader}>
                <div className={profileStyles.avatar}>{getInitials(userDetails.agency.companyName)}</div>
                <div className={profileStyles.headerText}>
                    <h2 className={profileStyles.headerTitle}>{userDetails.agency.companyName || 'Agency Details'}</h2>
                    <p className={profileStyles.headerSubtitle}>{userDetails.agency.tradingAsName || 'Agency Details'}</p>
                </div>
                <div className={profileStyles.headerActions}>
                    <PermissionGate requiredPermissions={["agency_manage_own"]}>
                        <button
                            className={`${buttonStyles.button} ${buttonStyles.primary}`}
                            onClick={() => setShowEditAgency(true)}
                        >
                            Edit Agency
                        </button>
                    </PermissionGate>
                </div>
            </div>

            <div className={profileStyles.cardBody}>
                <div className={profileStyles.fieldGrid}>
                    <div className={profileStyles.field}>
                        <span className={profileStyles.fieldLabel}>Company Name</span>
                        <span className={profileStyles.fieldValue}>{userDetails.agency.companyName}</span>
                    </div>
                    <div className={profileStyles.field}>
                        <span className={profileStyles.fieldLabel}>Your Role</span>
                        <span className={profileStyles.fieldValue}>{userDetails.role}</span>
                    </div>
                    <div className={profileStyles.field}>
                        <span className={profileStyles.fieldLabel}>Trading-As</span>
                        <span className={profileStyles.fieldValue}>{userDetails.agency.tradingAsName}</span>
                    </div>
                    <div className={profileStyles.field}>
                        <span className={profileStyles.fieldLabel}>Registration Number</span>
                        <span className={profileStyles.fieldValue}>{userDetails.agency.companyRegistrationNumber}</span>
                    </div>
                    <div className={profileStyles.field}>
                        <span className={profileStyles.fieldLabel}>Status</span>
                        <span className={`${profileStyles.badge} ${statusBadge.className}`}>{statusBadge.label}</span>
                    </div>
                    <div className={`${profileStyles.field} ${profileStyles.fieldFull}`}>
                        <span className={profileStyles.fieldLabel}>Physical Address</span>
                        <span className={profileStyles.fieldValue}>
                            {[userDetails.agency.streetNumber, userDetails.agency.streetName, userDetails.agency.suburbName, userDetails.agency.city, userDetails.agency.postalCode].filter(Boolean).join(', ')}
                        </span>
                    </div>
                </div>

                <PermissionGate requiredPermissions={["user_manage_agency"]}>
                    <AgencyUsers
                        agencyUsers={agencyUsers}
                        pendingInvites={pendingInvites}
                        userId={currentUserDetails.id}
                        removeLoading={removeLoading}
                        onRemoveUser={handleRemoveUser}
                        onDeleteUser={deleteAgencyUser}
                        onInviteUser={handleInviteUser}
                        inviteLoading={inviteLoading}
                        onCancelInvite={cancelInvite}
                        agency={currentUserDetails?.agency}
                    />
                </PermissionGate>
            </div>
            {showEditAgency && (
                <EditAgency
                    agencyDetails={userDetails.agency}
                    onAgencyUpdate={agency => {
                        setShowEditAgency(false);
                    }}
                    onCancel={() => setShowEditAgency(false)}
                />
            )}
        </div>
    );
};

export default AgencyInformation;
